import { Drawer, Box, SelectChangeEvent } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ModalTitle } from 'src/components/new/modal/ModalTitle';
import { ModalContent } from 'src/components/new/modal/ModalContent';
import { ModalFooter } from 'src/components/new/modal/ModalFooter';
import { TextField } from 'src/components/new/inputs/TextField';
import BkChipAutocomplete from 'src/components/autocomplete/BkChipAutocomplete';
import * as uuid from 'uuid';
import { Typography } from 'src/components/new/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BkToolTip from 'src/components/tooltip/BkToolTip';
import { checkForInvalidDomains } from 'src/utils/utility-functions';
import DeleteCustomerConfirmation from './DeleteOrganizationConfirmation';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import Select from 'src/components/new/inputs/Select';
import { organizationTypes } from 'src/utils/constant';

export interface EditableCustomer {
  name: string;
  type: string;
  customOrgType: string;
  sheetId: string;
  // printCredits: string;
  domains: { id: string; name: string }[];
}

interface EditCustomerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (profileInfo: EditableCustomer) => void;
  onDelete: () => void;
  info: EditableCustomer;
  sheets: { id: string; label: string }[];
}

interface EditCustomerErrors {
  nameError: string;
  domainsError: string;
}

export const EditCustomer: React.FC<EditCustomerProps> = ({ open, onClose, onSubmit, onDelete, info, sheets }) => {
  const [newCustomerInfo, setnewCustomerInfo] = useState<EditableCustomer>({
    name: '',
    type: '',
    customOrgType: '',
    sheetId: '',
    // printCredits: '',
    domains: [],
  });

  const [errors, setErrors] = useState<EditCustomerErrors>({
    nameError: '',
    domainsError: '',
  });

  const editOrganizationError = useSelector((state: RootState) => state.admin.editOrganizationError);

  useEffect(() => {
    if (editOrganizationError.nameExists) {
      setErrors((prevState) => ({ ...prevState, nameError: 'Name already exists' }));
    }
    if (editOrganizationError.domainError) {
      setErrors((prevState) => ({ ...prevState, domainsError: 'Domain already in use by another organization' }));
    }
  }, [editOrganizationError]);

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const setTags = (type: string, tags: string[]) => {
    const updatedDomains = tags.map((tag) => {
      const tagExists = newCustomerInfo.domains.find((domain) => domain.name === tag);
      return { id: tagExists ? tagExists.id : uuid.v4(), name: tag };
    });

    setnewCustomerInfo((prevState) => ({
      ...prevState,
      domains: updatedDomains,
    }));
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setnewCustomerInfo((prevState) => ({ ...prevState, [name]: value }));
  };

  useEffect(() => {
    setErrors({ nameError: '', domainsError: '' });
    if (open && info !== undefined) {
      const isCanonicalType = organizationTypes.includes(info.type);
      setnewCustomerInfo({
        ...info,
        type: isCanonicalType ? info.type : 'Other',
        customOrgType: isCanonicalType ? '' : info.type,
      });
    }

    return () => {
      if (!open) {
        setnewCustomerInfo({
          name: '',
          type: '',
          customOrgType: '',
          sheetId: '',
          // printCredits: '',
          domains: [],
        });
      }
    };
  }, [open]);

  const handleSubmit = () => {
    const newErrors: any = {};

    if (newCustomerInfo.name === '') {
      newErrors.nameError = 'Name cannot be empty';
    }

    const invalidDomains = checkForInvalidDomains(newCustomerInfo.domains);

    if (invalidDomains.length > 0) {
      newErrors.domainsError = `The following domains are invalid: ${invalidDomains.join(', ')}`;
    }

    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((error) => error !== '');
    if (!hasErrors) {
      const submittedInfo = {
        ...newCustomerInfo,
        type:
          newCustomerInfo.type === 'Other' && newCustomerInfo.customOrgType
            ? newCustomerInfo.customOrgType
            : newCustomerInfo.type,
      };
      onSubmit(submittedInfo);
    }
  };

  const onOrgTypeChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setnewCustomerInfo((prev) => ({
      ...prev,
      type: value,
      customOrgType: value !== 'Other' ? '' : prev.customOrgType,
    }));
  };

  const onPriceSheetChange = (e: SelectChangeEvent<string>) => {
    setnewCustomerInfo((prev) => ({ ...prev, sheetId: e.target.value }));
  };

  const deleteHandler = () => {
    onDelete();
    setShowConfirmationDialog(false);
  };
  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          background: '#FFF',
          width: '40%',
          maxWidth: '40%',
        },
      }}
    >
      <ModalTitle
        onDelete={() => setShowConfirmationDialog(true)}
        deleteButtonTitle="Delete organization"
        onClose={onClose}
      >
        Edit Organization
      </ModalTitle>
      <ModalContent>
        <Box style={{ marginBottom: '1rem', width: '100%' }}>
          <Box
            style={{
              marginBottom: '1rem',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TextField
              id="name"
              label="Organization Name"
              value={newCustomerInfo.name}
              name="name"
              error={errors.nameError}
              onChange={onChange}
            />
            <Select
              label="Organization Type"
              data={organizationTypes}
              onChange={onOrgTypeChange}
              value={newCustomerInfo.type}
              error=""
              disabled={false}
            />
            {newCustomerInfo.type === 'Other' && (
              <TextField
                id="customOrgType"
                placeholder="Please specify (optional)"
                value={newCustomerInfo.customOrgType}
                name="customOrgType"
                error=""
                onChange={onChange}
              />
            )}
            <Select
              label="Select Price Sheet"
              data={sheets}
              onChange={onPriceSheetChange}
              value={newCustomerInfo.sheetId?.toString()}
              error=""
              disabled={false}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '1rem' }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, fontFamily: 'Instrument Sans', lineHeight: '1.25rem', color: "#45464F" }}>
                Associated Domains
              </Typography>
              <BkToolTip
                Icon={InfoOutlinedIcon}
                title="Add domains that are officialy owned and controlled by your organization to ensure users are added appropriately"
              />
            </Box>
            <BkChipAutocomplete
              title="Organization Domains"
              tags={newCustomerInfo.domains.map((domain) => domain.name)}
              setTags={setTags}
              errorOnSubmit={''}
              type="organizationDomains"
              error={errors.domainsError}
            />
            {/* Remove until post-release
            <TextField
              id="printCredit"
              label="Print Credit"
              name="printCredit"
              value={newCustomerInfo.printCredits}
              error=""
              onChange={onChange}
            /> 
            */}
          </Box>
        </Box>
      </ModalContent>
      <ModalFooter onClose={onClose} onSubmit={handleSubmit} closeText="Cancel" submitText="Save" />
      <DeleteCustomerConfirmation
        onClose={() => setShowConfirmationDialog(false)}
        open={showConfirmationDialog}
        onSuccess={deleteHandler}
      />
    </Drawer>
  );
};
